import clsx from 'clsx';
import LazyImage from 'components/LazyImage';
import { MouseEventHandler, memo, useState } from 'react';
import ImageGallery from 'react-image-gallery';

const NavButton = memo(
  ({
    direction,
    disabled,
    onClick,
  }: {
    direction: 'left' | 'right';
    disabled: boolean;
    onClick: MouseEventHandler<HTMLElement>;
  }) => {
    return (
      <button
        type="button"
        className={`tw-rounded-full tw-bg-white/80 hover:tw-bg-white tw-transition-colors tw-duration-300 tw-h-8 tw-w-8 tw-absolute tw-z-20 tw-top-1/2 tw--translate-y-1/2 ${
          direction === 'left' ? 'tw-left-4' : 'tw-right-4'
        }`}
        disabled={disabled}
        onClick={onClick}
        aria-label={direction === 'left' ? 'Previous Slide' : 'Next Slide'}
      >
        <i className={`fa-solid fa-angle-${direction}`} />
      </button>
    );
  },
);

const PhotoGallery = ({ items, className }: { items: any; className?: string }) => {
  const [photoIndex, setPhotoIndex] = useState(0);
  return (
    <div
      className={clsx(
        'tw-relative tw-overflow-hidden tw-rounded-lg tw-h-full tw-w-full',
        className,
      )}
    >
      <ImageGallery
        items={items ?? []}
        lazyLoad
        showThumbnails={false}
        showFullscreenButton={false}
        showPlayButton={false}
        onBeforeSlide={setPhotoIndex}
        renderLeftNav={(onClick, disabled) => (
          <NavButton direction="left" onClick={onClick} disabled={disabled} />
        )}
        renderRightNav={(onClick, disabled) => (
          <NavButton direction="right" onClick={onClick} disabled={disabled} />
        )}
        renderItem={(item: NonNullable<typeof items>[number]) => (
          <div className="tw-h-full">
            <LazyImage
              src={item.originalImage}
              className="tw-w-full tw-h-full tw-object-cover"
              alt={item.originalAlt}
              title={item.originalTitle}
              loading={items?.length && item.index === 0 ? 'eager' : 'lazy'}
            />
          </div>
        )}
      />
      {items && items.length > 1 && (
        <div className="tw-absolute tw-bottom-4 tw-left-1/2 tw-transform tw--translate-x-1/2 tw-z-20">
          <div className="tw-flex tw-space-x-2">
            {items.map((_: any, i: number) => (
              <span
                key={i}
                className={`tw-w-2 tw-h-2 tw-rounded-full ${
                  i === photoIndex ? 'tw-bg-white' : 'tw-bg-white/50'
                }`}
              />
            ))}
          </div>
        </div>
      )}
      <style jsx global>{`
        .image-gallery,
        .image-gallery-content,
        .image-gallery-slide-wrapper,
        .image-gallery-swipe,
        .image-gallery-slides,
        .image-gallery-slide {
          height: 100%;
        }
        .image-gallery-slide {
          background: black;
        }
        .image-gallery-image {
          object-fit: contain;
          max-height: 100%;
          height: auto;
        }
      `}</style>
    </div>
  );
};

export { PhotoGallery };
